import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/auth';
import { supabase } from '@/integrations/supabase/client';

interface CreditInfo {
  balance: number;
  totalPurchased: number;
  isLoading: boolean;
  isLow: boolean;
  isEmpty: boolean;
  skipConfirmation: boolean;
}

interface UseCreditsReturn extends CreditInfo {
  refreshBalance: () => Promise<void>;
  canGeneratePattern: () => boolean;
  setSkipConfirmation: (skip: boolean) => Promise<void>;
}

/**
 * Hook for managing user credit balance
 * Used throughout the app to check and update credits
 */
export function useCredits(): UseCreditsReturn {
  const { user, isAuthenticated } = useAuth();
  const [balance, setBalance] = useState<number>(0);
  const [totalPurchased, setTotalPurchased] = useState<number>(0);
  const [skipConfirmation, setSkipConfirmationState] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch credit balance from profiles table
  const fetchBalance = useCallback(async () => {
    if (!user?.id) {
      setBalance(0);
      setTotalPurchased(0);
      setSkipConfirmationState(false);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('credit_balance, total_credits_purchased, skip_credit_confirmation')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching credit balance:', error);
        return;
      }

      if (data) {
        setBalance(data.credit_balance ?? 0);
        setTotalPurchased(data.total_credits_purchased ?? 0);
        setSkipConfirmationState(data.skip_credit_confirmation ?? false);
      }
    } catch (error) {
      console.error('Error in fetchBalance:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Refresh balance - exposed for use after purchases/generation
  const refreshBalance = useCallback(async () => {
    setIsLoading(true);
    await fetchBalance();
  }, [fetchBalance]);

  // Update skip confirmation preference
  const setSkipConfirmation = useCallback(async (skip: boolean) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ skip_credit_confirmation: skip })
        .eq('id', user.id);

      if (error) {
        console.error('Error updating skip confirmation:', error);
        return;
      }

      setSkipConfirmationState(skip);
    } catch (error) {
      console.error('Error in setSkipConfirmation:', error);
    }
  }, [user?.id]);

  // Check if user can generate a pattern
  const canGeneratePattern = useCallback(() => {
    return balance >= 1;
  }, [balance]);

  // Derived state
  const isLow = useMemo(() => balance > 0 && balance <= 2, [balance]);
  const isEmpty = useMemo(() => balance === 0, [balance]);

  // Fetch balance on mount and when user changes
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchBalance();
    } else {
      setBalance(0);
      setTotalPurchased(0);
      setSkipConfirmationState(false);
      setIsLoading(false);
    }
  }, [isAuthenticated, user?.id, fetchBalance]);

  return {
    balance,
    totalPurchased,
    isLoading,
    isLow,
    isEmpty,
    skipConfirmation,
    refreshBalance,
    canGeneratePattern,
    setSkipConfirmation,
  };
}
